import { samplesConfigObj } from '@/components/Glossary/Sections/SampleTypes/SamplesConfig';
import { preventNumberScroll } from '@/helpers';
import { _isEmptyString, _isNil, _notNil } from '@/littledash';
import { ErrorMessage } from '@hookform/error-message';
import type { FC } from 'react';
import { useFormContext } from 'react-hook-form@latest';

interface DetailFieldProps {
  detailKey: string;
  value?: string;
  unit?: string;
  fieldIndex: number;
  unitDisabled?: boolean;
}

export const DetailField: FC<DetailFieldProps> = ({ detailKey, value, unit, fieldIndex, unitDisabled }) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const keyName = `details.${fieldIndex}.key` as const;
  const valueName = `details.${fieldIndex}.value` as const;
  const unitName = `details.${fieldIndex}.unit` as const;
  return (
    <div className="w-50 pa2" data-test-component="DetailField" data-test-element="container" data-test-key={detailKey}>
      <label>{samplesConfigObj?.[detailKey]?.title}</label>
      <div className="flex">
        <input type="hidden" value={detailKey} {...register(keyName)} />
        <input
          type="number"
          onWheel={preventNumberScroll}
          className="w-100 mr2"
          data-test-element="number-input"
          step="any"
          style={{ marginBottom: 0 }}
          {...register(valueName, {
            validate: (val) => {
              if (_isNil(val) || _isEmptyString(val)) {
                return true;
              }
              return /^[-+]?[0-9]*\.?[0-9]+([eE][-+]?[0-9]+)?$/.test(String(val)) || 'Must be a valid number';
            },
          })}
          defaultValue={value}
        />
        {_notNil(samplesConfigObj?.[detailKey]?.units) && (
          <>
            {unitDisabled === true && <input type="hidden" value={unit} {...register(unitName)} />}
            <select
              className="w3"
              data-test-element="unit-select"
              {...(unitDisabled === true ? {} : register(unitName))}
              style={{ marginBottom: 0 }}
              defaultValue={unit}
              disabled={unitDisabled === true}
              data-tooltip-id="global-tooltip-id"
              data-tooltip-content={
                unitDisabled === true ? 'Units cannot be changed for samples linked to a sample plan' : undefined
              }
            >
              {samplesConfigObj[detailKey].units.map((configUnit: string) => (
                <option key={`${detailKey}_${configUnit}`} value={configUnit}>
                  {configUnit}
                </option>
              ))}
            </select>
          </>
        )}
      </div>
      <ErrorMessage
        errors={errors}
        name={valueName}
        render={({ message }) => <p className="f6 red db pt2">{message}</p>}
      />
    </div>
  );
};
